/**
 * Player-Backend: zapd-only.
 *
 * Das frühere PHP-HLS-Backend ist entfernt — zapd ist die einzige
 * Playback-Engine. Keine
 * Fallbacks, keine Mode-switches.
 *
 * API-pfade (alle unter `/api-zapd/`):
 *   POST   /api-zapd/play/start             {path, audio_streams[],
 *                                            subtitle_streams[], cmaf, dash}
 *                                           → {session_id, playlist_url, dash_url}
 *   GET    /api-zapd/play/{sid}/tracks      → {video, audio[], subtitles[]}
 *   POST   /api-zapd/play/{sid}/switch      {audio?, sub?, position?}
 *   DELETE /api-zapd/play/{sid}             cleanup
 *   POST   /api-zapd/play/{sid}/stop        sendBeacon-friendly stop
 *   GET    /api-zapd/play/{sid}/{gen}/{file} segments + manifests
 */

const ZAPD_BASE = '/api-zapd';

/**
 * fetch mit hartem Timeout (Robustness-Pattern "bound every network call").
 * Die Control-Plane-Calls (start/state/switch/keyframes/tracks) duerfen nicht
 * ewig haengen: ein haengendes /play/start oder /state stallt sonst STILL den
 * Player — der /state-Poll-Loop (lifecycle.js) feuert dann weder .then noch
 * .catch → kein Backoff, kein „Server neu verbinden"-Banner, kein Recover.
 * Bei Timeout abortet der Call → die Caller behandeln das wie einen Netzfehler.
 * Ein Caller-AbortSignal wird durchgereicht (Teardown bricht den Call mit ab).
 */
async function fetchWithTimeout(url, opts = {}, timeoutMs = 15000) {
  const ctl = new AbortController();
  const timer = setTimeout(
    () => ctl.abort(new DOMException('request timeout', 'TimeoutError')),
    timeoutMs,
  );
  if (opts.signal) {
    if (opts.signal.aborted) ctl.abort(opts.signal.reason);
    else opts.signal.addEventListener('abort', () => ctl.abort(opts.signal.reason), { once: true });
  }
  try {
    return await fetch(url, { ...opts, signal: ctl.signal });
  } finally {
    clearTimeout(timer);
  }
}

/**
 * @typedef {Object} StartResult
 * @property {string} sessionId
 * @property {string} playlistUrl     master.m3u8 (HLS)
 * @property {string|null} dashUrl    manifest-vod.mpd (DASH)
 * @property {object|null} raw        server-response für debug
 */

/**
 * Open a new playback session.
 *
 * @param {{
 *   path: string,
 *   segdur?: number,
 *   transcode?: 'auto'|'copy'|'h264',
 *   audio?: number,                  default-audio-input-stream-idx
 *   audio_streams?: Array<number>,   alle audios als renditions im master
 *   subtitle_streams?: Array<number>,alle extractable subs
 *   cmaf?: boolean,                  default true
 *   dash?: boolean,                  default true
 * }} opts
 * @returns {Promise<StartResult>}
 */
export async function start(opts) {
  const body = { path: opts.path };
  // Z4 (Contract 02 §4.1): torrent://<info_hash>/<file_index>- und
  // rar://-Sources brauchen ein explizites kind, damit der Server zur
  // TorrentSource/RarSource routet statt den Pfad als Datei zu öffnen.
  if (opts.kind) body.kind = opts.kind;
  else if (body.path.startsWith('torrent://')) body.kind = 'torrent';
  else if (body.path.startsWith('rar://')) body.kind = 'rar';
  if (typeof opts.segdur === 'number') body.segdur = opts.segdur;
  if (opts.transcode) body.transcode = opts.transcode;
  if (typeof opts.audio === 'number') body.audio = opts.audio;
  if (typeof opts.audio_lang === 'string') body.audio_lang = opts.audio_lang;
  // Every audio track as its own rendition: the native shell keeps the viewer's
  // language across films by switching locally (2026-09-20).
  if (opts.all_audio) body.all_audio = true;
  if (Array.isArray(opts.audio_streams)) body.audio_streams = opts.audio_streams;
  if (Array.isArray(opts.subtitle_streams)) body.subtitle_streams = opts.subtitle_streams;
  if (opts.cmaf !== undefined) body.cmaf = !!opts.cmaf;
  if (opts.dash !== undefined) body.dash = !!opts.dash;
  // Z2-ABR: Multi-Variant-Ladder anfordern (per-Request, zapd clamp 1-6,
  // Default 1 = single-variant). >1 → Master mit N Renditions, den der
  // BOLA-Controller (player-worker) bedient. Contract 02 §8.
  if (typeof opts.abr === 'number') body.abr = opts.abr;
  // Height cap for the transcode. Load-bearing, not cosmetic: without it the server
  // passes the ORIGINAL through (an ABR ladder exists only on the encode path), so a
  // thin line gets the full-bitrate file no matter which rung the client picks
  // (measured 2026-09-20: declared 2.9 Mbit/s, delivered 16.4).
  if (typeof opts.maxHeight === 'number' && opts.maxHeight > 0) body.maxHeight = opts.maxHeight;
  // Phase 6a: client capability handshake. Server uses these flags to
  // decide passthrough vs transcode per-stream. Without caps the server
  // falls back to legacy transcode-everything behavior.
  if (opts.client_caps) body.client_caps = opts.client_caps;

  const r = await fetchWithTimeout(
    ZAPD_BASE + '/play/start',
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: opts.signal, // Player-Teardown bricht den Call ab
    },
    30000,
  ); // grosszuegig: Session-Create kann ISO-Mount/Probe enthalten
  if (!r.ok) {
    const txt = await r.text().catch(() => '');
    throw new Error(`zapd play/start ${r.status}: ${txt || r.statusText}`);
  }
  const data = await r.json();
  return {
    sessionId: data.session_id,
    playlistUrl: data.playlist_url,
    dashUrl: data.dash_url || null,
    seekReady: !!data.seek_ready,
    keyframesEtaMs: Number(data.keyframes_eta_ms || 0),
    cacheStatus: data.cache_status || 'unknown',
    // M1.5 — server-declared seekability. Defaults true on legacy
    // responses where the field is absent. When false (= the play-
    // during-extract scenario, future), the player should grey-out
    // the scrub-bar. Wired up in zapd-engine-bridge.js's video.seekable
    // override + ZapPlayer UI.
    seekable: data.seekable !== false,
    // Z4 (Contract 02 §4.1): {kind, progress} der Wiedergabe-Quelle —
    // gesetzt fuer torrent/rar-Sources (play-while-download), sonst absent.
    // Treibt das Source-Badge (ui/source-badge.js). progress = 0..100.
    source: data.source || null,
    raw: data,
  };
}

/**
 * Direct-play für Disc-Images (nativer Shell): zapd loop-mountet die .iso
 * server-seitig + löst den Haupttitel-.m2ts auf, den der native Engine DIREKT
 * öffnet (kein HLS-Transcode → native Dekodierung + byte-range Seek). HLS
 * (start()) bleibt für den Browser/WASM-Player.
 * @param {{path:string, kind?:string, signal?:AbortSignal}} opts
 * @returns {Promise<{sessionId:string, streamUrl:string, mount:string, seekIndex:object, raw:object}>}
 */
export async function discOpen(opts) {
  const body = { path: opts.path };
  if (opts.kind) body.kind = opts.kind;
  // Play-while-download: for a torrent-backed .iso the server needs the disc
  // header (UDF descriptors at the .iso's start/end) on disk before udfread can
  // parse the volume. While it isn't there yet it answers 503 (buffering) + keeps
  // priming that region; retry until it can parse or we give up (~3 min). Each
  // 503 pings opts.onBuffering(attempt); a 201 pings onBuffering(null) to clear.
  const deadline = Date.now() + (opts.bufferTimeoutMs || 180000);
  for (let attempt = 0; ; attempt++) {
    const r = await fetchWithTimeout(
      ZAPD_BASE + '/disc/open',
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal: opts.signal, // Teardown bricht den (Mount-enthaltenden) Call ab
      },
      30000,
    );
    if (r.ok) {
      const data = await r.json();
      if (opts.onBuffering) opts.onBuffering(null);
      return {
        sessionId: data.session_id,
        streamUrl: data.stream_url,
        mount: data.mount || '',
        seekIndex: data.seek_index || null, // {t:[us], b:[byte], count, base_us} — Blu-ray EP_map
        trackLanguages: data.track_languages || {}, // MPEG-TS PID → ISO 639 code
        raw: data,
      };
    }
    if (r.status === 503 && Date.now() < deadline) {
      await r.text().catch(() => ''); // drain the body so the connection frees
      let retrySec = 2;
      const ra = parseInt(r.headers.get('Retry-After') || '', 10);
      if (!Number.isNaN(ra) && ra > 0) retrySec = Math.min(ra, 5);
      if (opts.onBuffering) opts.onBuffering(attempt + 1);
      await new Promise((res, rej) => {
        const t = setTimeout(res, retrySec * 1000);
        if (opts.signal) {
          opts.signal.addEventListener(
            'abort',
            () => {
              clearTimeout(t);
              rej(new DOMException('aborted', 'AbortError'));
            },
            { once: true },
          );
        }
      });
      continue;
    }
    const txt = await r.text().catch(() => '');
    throw new Error(`zapd disc/open ${r.status}: ${txt || r.statusText}`);
  }
}

/**
 * Direct-play für normale Container (nativer Shell): zapd öffnet die Original-
 * Mediendatei server-seitig (Gate: requireUser + Privacy::canRead + Ownership)
 * und serviert sie ROH per HTTP-Byte-Range. Die native libav-Engine demuxt sie
 * DIREKT (alle Tracks, DTS/HEVC/HDR, native Byte-Range-Seek) — KEIN HLS, KEIN
 * Transcode. EINE URL, kein Manifest. HLS (start()) bleibt für Browser/WASM.
 * @param {{path:string, kind?:string, signal?:AbortSignal}} opts
 * @returns {Promise<{sessionId:string, streamUrl:string, raw:object}>}
 */
export async function streamOpen(opts) {
  const body = { path: opts.path };
  if (opts.kind) body.kind = opts.kind;
  // T1 — /stream/open may 503 while it harvests the seek index (priming the
  // torrent's Cues/moov region); retry until it's ready or we give up (~3 min),
  // mirroring discOpen. Each 503 pings opts.onBuffering(attempt); 201 pings null.
  const deadline = Date.now() + (opts.bufferTimeoutMs || 180000);
  for (let attempt = 0; ; attempt++) {
    const r = await fetchWithTimeout(
      ZAPD_BASE + '/stream/open',
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal: opts.signal, // Teardown bricht den Open-Call ab
      },
      30000,
    );
    if (r.ok) {
      const data = await r.json();
      if (opts.onBuffering) opts.onBuffering(null);
      return {
        sessionId: data.session_id,
        streamUrl: data.stream_url, // relativ (/api-zapd/stream/<sid>) — Bridge hängt Origin + Token an
        // A-P1.1 Browser-DirectPlay: total file size (uint64) for SAB-AVIO readahead
        // + AVSEEK_SIZE. Absent on error → 0 → the client treats it as "not
        // direct-capable" and stays on HLS (deploy-order-independent).
        fileSize: Number(data.file_size) || 0,
        // T1 — time→byte keyframe index (mkv Cues / m2ts .clpi) so the engine
        // seeks by exact byte. Absent/empty for mp4 (native moov seek) → the
        // engine keeps its timestamp seek. Shape {t:[us],b:[byte],count}.
        seekIndex: data.seek_index || null,
        raw: data,
      };
    }
    if (r.status === 503 && Date.now() < deadline) {
      await r.text().catch(() => ''); // drain so the connection frees
      let retrySec = 2;
      const ra = parseInt(r.headers.get('Retry-After') || '', 10);
      if (!Number.isNaN(ra) && ra > 0) retrySec = Math.min(ra, 5);
      if (opts.onBuffering) opts.onBuffering(attempt + 1);
      await new Promise((res, rej) => {
        const t = setTimeout(res, retrySec * 1000);
        if (opts.signal) {
          opts.signal.addEventListener(
            'abort',
            () => {
              clearTimeout(t);
              rej(new DOMException('aborted', 'AbortError'));
            },
            { once: true },
          );
        }
      });
      continue;
    }
    const txt = await r.text().catch(() => '');
    throw new Error(`zapd stream/open ${r.status}: ${txt || r.statusText}`);
  }
}

/**
 * T1 async — poll the seek index zapd harvests in the BACKGROUND after a torrent
 * /stream/open (the inline harvest used to cost seconds of time-to-first-frame:
 * it waited for the mkv Cues at the file END of a torrent that just started).
 * Resolves with {t:[us],b:[byte],count} once ready, or null (none exists /
 * timeout / abort / session gone). Playback never waits on this — the caller
 * injects the index into the LIVE pipeline via shell.setByteIndex.
 * @param {string} sessionId
 * @param {{signal?:AbortSignal, timeoutMs?:number}} [opts]
 * @returns {Promise<{t:number[], b:number[], count:number}|null>}
 */
export async function pollSeekIndex(sessionId, opts = {}) {
  const deadline = Date.now() + (opts.timeoutMs || 45000);
  let wait = 1000;
  for (;;) {
    let r = null;
    try {
      r = await fetchWithTimeout(
        ZAPD_BASE + '/stream/' + sessionId + '/seekindex',
        { credentials: 'include', signal: opts.signal },
        10000,
      );
    } catch (e) {
      if (e && e.name === 'AbortError') return null;
      // transient network error → keep polling until the deadline
    }
    if (r) {
      if (r.status === 404) return null; // session reaped/foreign — stop polling
      if (r.ok) {
        const data = await r.json().catch(() => null);
        if (data && data.ready) {
          const idx = data.seek_index;
          return idx && Number(idx.count) >= 2 ? idx : null;
        }
      } else {
        await r.text().catch(() => ''); // drain so the connection frees
      }
    }
    if (Date.now() + wait > deadline) return null;
    await new Promise((res) => {
      const t = setTimeout(res, wait);
      if (opts.signal)
        opts.signal.addEventListener(
          'abort',
          () => {
            clearTimeout(t);
            res();
          },
          { once: true },
        );
    });
    if (opts.signal && opts.signal.aborted) return null;
    wait = Math.min(wait * 2, 4000);
  }
}

/**
 * Release a /stream/<sid> session (A-P1.1). Best-effort DELETE, mirrors
 * stopAsync — used when a direct-attach is abandoned (unsupported → HLS
 * fallback) or the player tears down. The 5-min idle reaper covers a dropped
 * request.
 */
export async function streamClose(sid) {
  if (!sid) return;
  try {
    await fetchWithTimeout(
      ZAPD_BASE + '/stream/' + encodeURIComponent(sid),
      {
        method: 'DELETE',
        credentials: 'include',
      },
      8000,
    );
  } catch {
    /* ignore */
  }
}

/**
 * GET /play/{sid}/state — schmaler poll-endpoint für indexing-progress.
 */
export async function fetchState(sid, signal) {
  const r = await fetchWithTimeout(
    ZAPD_BASE + '/play/' + encodeURIComponent(sid) + '/state',
    { credentials: 'include', signal },
    10000,
  );
  if (!r.ok) throw new Error('zapd play/state ' + r.status);
  return await r.json();
}

/**
 * GET /play/{sid}/keyframes — keyframe-PTS-array (microseconds) for
 * VLC-fast-seek. Returns null if scan still in progress (= 503).
 */
export async function keyframes(sid) {
  if (!sid) return null;
  const r = await fetchWithTimeout(
    ZAPD_BASE + '/play/' + encodeURIComponent(sid) + '/keyframes',
    {
      credentials: 'include',
    },
    10000,
  );
  if (r.status === 503) return null; // scan still running
  if (!r.ok) throw new Error(`zapd /keyframes ${r.status}`);
  return await r.json(); // { keyframes_us: number[], total_duration_us: number }
}

/**
 * GET /play/{sid}/tracks — listet alle audio + subtitle tracks mit
 * rendition-URLs für active.
 */
export async function tracks(sid) {
  const r = await fetchWithTimeout(
    ZAPD_BASE + '/play/' + encodeURIComponent(sid) + '/tracks',
    {
      credentials: 'include',
    },
    10000,
  );
  if (!r.ok) throw new Error(`zapd /tracks ${r.status}`);
  return await r.json();
}

/**
 * Track-Switch (audio + sub + position) via encoder-restart.
 *
 * Phase 8: server unterscheidet zwei modi anhand request-shape:
 *   - audio-only (= body hat `audio` aber NICHT `sub`): server swappt
 *     audio-pipeline ohne gen-bump, video-pipeline läuft weiter.
 *     Response: `{mode: "audio_swap", audio_rendition_url, generation}`.
 *   - full switch (= body hat `sub`): full gen-bump.
 *     Response: `{mode: "gen_bump", playlist_url, generation}`.
 *
 * Returns the parsed JSON body so caller kann mode + URLs auswerten.
 */
export async function switchTracks(sid, opts) {
  const body = {};
  if (typeof opts.audio === 'number') body.audio = opts.audio;
  if (typeof opts.sub === 'number') body.sub = opts.sub;
  if (typeof opts.position === 'number') body.position = opts.position;
  const r = await fetchWithTimeout(
    ZAPD_BASE + '/play/' + encodeURIComponent(sid) + '/switch',
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
    20000,
  ); // gen-bump/encoder-restart kann etwas dauern
  if (!r.ok) {
    const txt = await r.text().catch(() => '');
    throw new Error(`zapd /switch ${r.status}: ${txt || r.statusText}`);
  }
  try {
    return await r.json();
  } catch {
    return {};
  }
}

/**
 * sendBeacon-stop. Wird beim pagehide / visibilitychange-grace gerufen.
 * sendBeacon kann nur POST — drogon hat /play/{sid}/stop als POST-route
 * die intern markForDeletion macht (gleicher reaper-pfad wie DELETE).
 */
export function stopBeacon(sid) {
  if (!sid) return false;
  try {
    const blob = new Blob([], { type: 'application/json' });
    return navigator.sendBeacon(ZAPD_BASE + '/play/' + encodeURIComponent(sid) + '/stop', blob);
  } catch {
    return false;
  }
}

/** Synchronous DELETE — explicit stop (z.B. modal close). */
export async function stopAsync(sid) {
  if (!sid) return;
  try {
    await fetchWithTimeout(
      ZAPD_BASE + '/play/' + encodeURIComponent(sid),
      {
        method: 'DELETE',
        credentials: 'include',
      },
      8000,
    );
  } catch {
    /* ignore */
  }
}

/**
 * Hard-reload — invalidates the keyframe-cache for the session's path
 * + stops the session. Use when manifest cache is corrupted / out-of-sync
 * with reality (= e.g. after file replacement, or seg-out-of-range bugs).
 * Caller should follow up with a fresh start() to play again.
 */
export async function hardReload(sid) {
  if (!sid) return;
  try {
    await fetchWithTimeout(
      ZAPD_BASE + '/play/' + encodeURIComponent(sid) + '/hard-reload',
      {
        method: 'POST',
        credentials: 'include',
      },
      8000,
    );
  } catch {
    /* ignore */
  }
}
